import { ChevronLeft, ChevronRight, Search, Store } from 'lucide-react';
import { useEffect, useState } from 'react';
import { send } from '../../components';
import { Button } from '../../components/ui/button';
import { PartnerShell } from './PartnerShell';

const PartnerMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [meta, setMeta] = useState({ total: 0, current_page: 1, last_page: 1, per_page: 20 });
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const loadMerchants = async (page = 1) => {
    const res = await send('io0021', {
      search: query,
      page,
      per_page: meta.per_page,
    }, setLoading);

    setMerchants(res?.data || []);
    setMeta({
      total: res?.total || 0,
      current_page: res?.current_page || page,
      last_page: res?.last_page || 1,
      per_page: res?.per_page || meta.per_page,
    });
  };

  useEffect(() => {
    loadMerchants(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSearch = (event) => {
    event.preventDefault();
    setQuery(search.trim());
  };

  const goToPage = (page) => {
    if (page < 1 || page > meta.last_page || loading) return;
    loadMerchants(page);
  };

  return (
    <PartnerShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-medium text-primary">Merchants</p>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">Partner merchants</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Merchants created by your Partner organization.
            </p>
          </div>

          <form onSubmit={handleSearch} className="flex w-full gap-2 md:w-auto">
            <div className="relative min-w-0 flex-1 md:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Search merchants"
              />
            </div>
            <Button type="submit" disabled={loading}>Search</Button>
          </form>
        </div>

        <section className="overflow-hidden rounded-lg border border-border bg-white">
          <div className="overflow-x-auto">
            {!loading && merchants.length > 0 && (
              <table className="w-full min-w-[760px] text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Business name</th>
                    <th className="px-4 py-3 text-left font-semibold">Partner ref</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Registered</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((merchant) => (
                    <tr key={merchant.id} className="border-b border-border last:border-b-0">
                      <td className="px-4 py-3 font-medium text-foreground">{merchant.businessName || '-'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">{merchant.partnerMerchantRef || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-md px-2 py-1 text-xs font-medium ${
                          merchant.status === 'active'
                            ? 'bg-success/10 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {merchant.status || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{merchant.registeredAt || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{merchant.email || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{merchant.phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && merchants.length === 0 && (
              <div className="flex min-h-64 flex-col items-center justify-center px-4 py-12 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Store className="h-6 w-6" />
                </div>
                <h2 className="text-base font-semibold text-foreground">No merchants found</h2>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  Merchants created through the Partner API will appear here.
                </p>
              </div>
            )}

            {loading && (
              <div className="flex min-h-64 items-center justify-center px-4 py-12 text-sm text-muted-foreground">
                Loading merchants...
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
            <div>
              Total: <span className="font-medium text-foreground">{meta.total}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => goToPage(meta.current_page - 1)} disabled={loading || meta.current_page <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>Page {meta.current_page} of {meta.last_page}</span>
              <Button variant="outline" size="sm" onClick={() => goToPage(meta.current_page + 1)} disabled={loading || meta.current_page >= meta.last_page}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </PartnerShell>
  );
};

export default PartnerMerchants;
